import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { TfiTarget } from "react-icons/tfi";
import { averageSimple, standardDeviation } from 'simple-statistics';
import { FormatValue, isMobile, ValueFormat } from "../../../app/utils";

export interface DataPoint {
  x: Date;
  y: number;
  label?: string;
}

interface DataPointHighlight {
  datapoint: DataPoint;
  color: d3.Color;
  xPosition: number;
  yPosition: number;
}

interface ToolTip {
  xPosition: number,
  yPosition: number,
  xValue: Date,
  yValue: number,
  side: "right" | "left",
  label?: string,
}

interface LineChartProps {
  originalData: DataPoint[],
  xDescription?: string,
  yDescription?: string,
  yFormat: ValueFormat
  zScoreEnabled: boolean
  showMinMaxValues: boolean
}

const LineChart: React.FC<LineChartProps> = (props: LineChartProps) => {
  const ref = useRef<SVGSVGElement | null>(null);
  const [toolTip, setToolTip] = useState<ToolTip | null>(null);
  const [maxValueDataPoint, setMaxValueDataPoint] = useState<DataPointHighlight | null>(null);
  const [minValueDataPoint, setMinValueDataPoint] = useState<DataPointHighlight | null>(null);
  const [showToolTipOpacity, setShowToolTipOpacity] = useState<boolean>(false);
  const [showToolTipVisible, setShowToolTipVisible] = useState<boolean>(false);
  const [touchMoving, setTouchMoving] = useState(false);
  const [touchTimeout, setTouchTimeout] = useState<NodeJS.Timeout | undefined>(undefined);
  const [data, setData] = useState(props.originalData);

  // Original data modified
  useEffect(() => {
    if (props.zScoreEnabled) {
      const values = props.originalData.map(dataPoint => dataPoint.y);
      const average = averageSimple(values);
      const stdDeviation = standardDeviation(values);
      setData(props.originalData.map((dataPoint, index) => {
        return {
          x: dataPoint.x,
          y: Math.round((dataPoint.y - average) / stdDeviation * 100) / 100,
          label: dataPoint.label,
        } as DataPoint;
      }));
    } else {
      setData(props.originalData);
    }
  }, [props.originalData, props.zScoreEnabled])

  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  const [minX, setMinX] = useState(new Date());
  const [maxX, setMaxX] = useState(new Date());

  const [minY, setMinY] = useState(0);
  const [maxY, setMaxY] = useState(0);

  const [marginMinX, setMarginMinX] = useState(new Date());
  const [marginMaxX, setMarginMaxX] = useState(new Date());

  const [marginMinY, setMarginMinY] = useState(0);
  const [marginMaxY, setMarginMaxY] = useState(0);

  // Chart data or z-score modified
  useEffect(() => {
    if (!ref.current || !data) return;

    const lineToAxisMargin = 0.05;

    const minValueX = Math.min(...data.map(dataPoint => dataPoint.x.valueOf()));
    const maxValueX = Math.max(...data.map(dataPoint => dataPoint.x.valueOf()));

    const minX = new Date(minValueX);
    const maxX = new Date(maxValueX);
    const marginMinX = new Date(minValueX - (maxValueX - minValueX) * lineToAxisMargin);
    const marginMaxX = new Date(maxValueX + (maxValueX - minValueX) * lineToAxisMargin);

    const zScoreMin = !props.zScoreEnabled ? 0 : Math.max(-5, Math.trunc(data.map(dataPoint => dataPoint.y).reduce((prev, cur) => Math.min(prev, cur), 0)) - 1);
    const zScoreMax = !props.zScoreEnabled ? 0 : Math.min(5, Math.trunc(data.map(dataPoint => dataPoint.y).reduce((prev, cur) => Math.max(prev, cur), 0)) + 1);
    const minY = props.zScoreEnabled ? zScoreMin : Math.min(...data.map(dataPoint => dataPoint.y));
    const maxY = props.zScoreEnabled ? zScoreMax : Math.max(...data.map(dataPoint => dataPoint.y));
    const marginMinY = minY - (maxY - minY) * lineToAxisMargin;
    const marginMaxY = maxY + (maxY - minY) * lineToAxisMargin;

    setMinX(minX);
    setMaxX(maxX);

    setMarginMinX(marginMinX);
    setMarginMaxX(marginMaxX);

    setMinY(minY);
    setMaxY(maxY);

    setMarginMinY(marginMinY);
    setMarginMaxY(marginMaxY);

    // 🔹 Lê o tamanho real do SVG definido pelo Tailwind
    const { width, height } = ref.current.getBoundingClientRect();
    setWidth(width);
    setHeight(height);

    const svg = d3.select(ref.current);

    // 🔹 Escalas
    const xScaleLine = d3.scaleTime()
      .domain([marginMinX, maxX])
      .range([0.1 * width, 0.9 * width]);
    const yScaleLine = d3.scaleLinear()
      .domain([marginMinY, marginMaxY])
      .range([0.9 * height, 0.1 * height]);

    // If showMinMaxValues is set to true, set the X and Y values for the target icon to be placed.
    if (props.showMinMaxValues && props.originalData.length > 0) {
      const max = data.reduce((prev, cur) => prev.y > cur.y ? prev : cur, props.originalData[0]);
      const min = data.reduce((prev, cur) => prev.y < cur.y ? prev : cur, props.originalData[0]);
      const xPositionMax = xScaleLine(max.x.valueOf());
      const yPositionMax = yScaleLine(max.y);
      const xPositionMin = xScaleLine(min.x.valueOf());
      const yPositionMin = yScaleLine(min.y);
      setMaxValueDataPoint({ datapoint: max, xPosition: xPositionMax, yPosition: yPositionMax, color: d3.rgb(252, 165, 165) });
      setMinValueDataPoint({ datapoint: min, xPosition: xPositionMin, yPosition: yPositionMin, color: d3.rgb(134, 239, 172) });
    }

    // 🔹 Linha
    const line = d3.line<DataPoint>()
      .x(d => xScaleLine(d.x))
      .y(d => yScaleLine(d.y))
      .curve(d3.curveCatmullRom.alpha(0.5));

    const existingLine = svg.select(".line");

    if (existingLine.empty()) {
      svg.selectAll("*").remove();
      svg
        .attr("width", width)
        .attr("height", height);

      const defs = svg.append("defs");

      // 🔹 Gradiente que se move ao longo da linha
      const lineGradient = defs.append("linearGradient")
        .attr("id", "lineGradient")
        .attr("x1", "0%")
        .attr("y1", "0%")
        .attr("x2", "100%")
        .attr("y2", "0%");

      // stops fixos, mas com offset animado
      lineGradient.append("stop")
        .attr("offset", "0%")
        .attr("stop-color", "#60a5fa");

      lineGradient.append("stop")
        .attr("offset", "70%")
        .attr("stop-color", "#bfdbfe");

      lineGradient.append("stop")
        .attr("offset", "80%")
        .attr("stop-color", "#ffffff");

      lineGradient.append("stop")
        .attr("offset", "95%")
        .attr("stop-color", "#ffffff");

      lineGradient.append("stop")
        .attr("offset", "100%")
        .attr("stop-color", "#60a5fa");

      // animação para deslocar o gradiente
      lineGradient.append("animateTransform")
        .attr("attributeName", "gradientTransform").attr("id", "wave")
        .attr("type", "translate")
        .attr("from", "-1 0")
        .attr("to", "2 0")
        .attr("begin", "1s")
        .attr("dur", "5s")
        .attr("repeatCount", "indefinite");

      // 🔹 Filtro de brilho
      const glow = defs.append("filter").attr("id", "glow");
      glow.append("feGaussianBlur")
        .attr("in", "SourceGraphic")
        .attr("stdDeviation", "1.5")
        .attr("result", "blur")
        .append("animate")
        .attr("attributeName", "stdDeviation")
        .attr("values", "1.5;3;2;1.5;1.5;1.5")
        .attr("begin", "1s")
        .attr("dur", "5s")
        .attr("repeatCount", "indefinite");

      glow.append("feMerge")
        .selectAll("feMergeNode")
        .data(["blur", "SourceGraphic", "blur"])
        .enter()
        .append("feMergeNode")
        .attr("in", d => d);

      // Cria o a linha svg
      svg.append("path")
        .datum(data)
        .attr("class", "line")
        .attr("d", line)
        .attr("fill", "none")
        .attr("stroke", "url(#lineGradient)")
        .attr("stroke-width", 1)
        .attr("filter", "url(#glow)");

      // 🔹 Inicializa os eixos X e Y
      const xScaleAxis = d3.scaleTime()
        .domain([marginMinX, marginMaxX])
        .range([0.1 * width, (0.9 + lineToAxisMargin) * width]);
      const xAxis = d3.axisBottom(xScaleAxis)
        .ticks(6)
        .tickSize(6)
        .tickSizeOuter(0)
        .tickFormat(d => {
          if (d.valueOf() > new Date().valueOf()) {
            return "";
          } else if (d instanceof Date) {
            return d3.timeFormat("%Y")(d);
          } else {
            return d3.timeFormat("%Y")(new Date(d.valueOf()));
          }
        });
      const yScaleAxis = d3.scaleLinear()
        .domain([marginMinY, marginMaxY])
        .range([0.9 * height, 0.1 * height]);
      const yAxis = d3.axisLeft(yScaleAxis)
        .ticks(5)
        .tickSize(6)
        .tickSizeOuter(0);

      svg.append("g")
        .attr("transform", "translate(0," + 0.9 * height + ")")
        .attr("class", "myXaxis")
        .call(xAxis);

      svg.append("g")
        .attr("transform", "translate(" + 0.1 * width + ", 0)")
        .attr("class", "myYaxis")
        .call(yAxis);

      svg.selectAll(".myXaxis")
        .style("stroke", props.zScoreEnabled ? "RGBA(212, 212, 212, 0.7)" : "#d4d4d4")
        .style("font-size", isMobile() ? "11px" : "14px")
        .style("font-weight", "100")
        .style("font-family", "Arial");

      svg.selectAll(".myYaxis")
        .style("stroke", "#d4d4d4")
        .style("font-size", isMobile() ? "11px" : "14px")
        .style("font-weight", "100")
        .style("font-family", "Arial");
    } else {
      // Atualiza a linha svg existente com novos dados
      existingLine
        .datum(data)
        .transition() // opcional, para animação
        .duration(1000)
        .attr("d", line);

      // Atualiza o eixo Y
      const yScaleAxis = d3.scaleLinear()
        .domain([marginMinY, marginMaxY])
        .range([0.9 * height, 0.1 * height]);
      const yAxisChangeScale = d3.axisLeft(yScaleAxis)
        .ticks(props.zScoreEnabled ? (zScoreMax - zScoreMin) : 5)
        .tickSize(props.zScoreEnabled ? (-0.85 * width) : 6)
        .tickSizeOuter(0)
        .tickFormat(d => (props.zScoreEnabled && d === 0) ? "0" : props.zScoreEnabled ? `${d3.format("d")(d)} σ` : props.yFormat === "percentage" ? `${d3.format(".0%")(d)}` : `${d3.format("d")(d)}`);
      svg.select(".myYaxis")
        .transition()
        .duration(1000)
        .call(yAxisChangeScale as any);
      svg.selectAll(".myYaxis .tick line")
        .transition()
        .duration(1000)
        .style("stroke", props.zScoreEnabled ? "RGBA(252, 252, 252, 0.2)" : "#d4d4d4");

      // Atualiza o eixo x
      const xScaleAxis = d3.scaleTime()
        .domain([marginMinX, marginMaxX])
        .range([0.1 * width, (0.9 + lineToAxisMargin) * width]);
      const xAxisChangeScale = d3.axisBottom(xScaleAxis)
        .ticks(6)
        .tickSize(6)
        .tickSizeOuter(0)
        .tickFormat(d => {
          if (d.valueOf() > new Date().valueOf()) {
            return "";
          } else if (d instanceof Date) {
            return d3.timeFormat("%Y")(d);
          } else {
            return d3.timeFormat("%Y")(new Date(d.valueOf()));
          }
        });
      svg.select(".myXaxis")
        .transition()
        .duration(1000)
        .call(xAxisChangeScale as any);
    }
  }, [data, props.zScoreEnabled]);

  function chartMouseMove(event: React.MouseEvent<any>): void {
    try {
      const rect = ref.current!.getBoundingClientRect();
      const px = event.clientX - rect.left;
      setToolTipValues(px);
      setTouchMoving(true);
      setShowToolTipVisible(true);
      setTimeout(() => {
        setShowToolTipOpacity(true);
      }, 200);
    } catch (e) {
      console.log(e);
    }
  }

  function chartTouchStart(event: React.TouchEvent<SVGElement>): void {
    try {
      const rect = ref.current!.getBoundingClientRect();
      const px = event.changedTouches[0].clientX - rect.left;
      setToolTipValues(px);
      setTouchMoving(false);
      if (touchTimeout) {
        clearTimeout(touchTimeout);
        setTouchTimeout(undefined);
      }
      setShowToolTipVisible(true);
      setShowToolTipOpacity(true);
      const timeout = setTimeout(() => {
        setTouchMoving(false);
        setShowToolTipOpacity(false);
        setTimeout(() => {
          setShowToolTipVisible(false);
        }, 200)
      }, 1500);
      setTouchTimeout(timeout);
    } catch (e) {
      console.log(e);
    }
  }

  function chartTouchMove(event: React.TouchEvent<SVGElement>): void {
    try {
      const rect = ref.current!.getBoundingClientRect();
      const px = event.changedTouches[0].clientX - rect.left;
      setToolTipValues(px);
      setTouchMoving(true);
      if (touchTimeout) {
        clearTimeout(touchTimeout);
        setTouchTimeout(undefined);
      }
      setShowToolTipVisible(true);
      setShowToolTipOpacity(true);
    } catch (e) {
      console.log(e);
    }
  }

  function setToolTipValues(clientPositionX: number) {
    try {
      const xScaleLine = d3.scaleTime()
        .domain([marginMinX, maxX])
        .range([0.1 * width, 0.9 * width]);
      const yScaleLine = d3.scaleLinear()
        .domain([marginMinY, marginMaxY])
        .range([0.9 * height, 0.1 * height]);
      const xValueProxy = xScaleLine?.invert(clientPositionX);
      const dataPoint = data.findLast(d => d.x.valueOf() <= xValueProxy.valueOf()) ?? data[0];
      const xValue = dataPoint?.x;
      const yValue = dataPoint?.y;
      const label = dataPoint?.label;
      const xPosition = xScaleLine(xValue);
      const yPosition = yScaleLine(yValue);
      const side = clientPositionX > xScaleLine.range().reduce((prev, cur) => prev + cur, 0) / 2 ? "left" : "right";
      setToolTip({
        xPosition,
        yPosition,
        xValue,
        yValue,
        side,
        label,
      });
    } catch (e) {
      console.log(e);
    }
  }

  function chartTouchEnd(event: any): void {
    try {
      if (touchMoving) {
        setTouchMoving(false);
        const timeout = setTimeout(() => {
          setShowToolTipOpacity(false);
          setTimeout(() => {
            setShowToolTipVisible(false);
          }, 200)
        }, isMobile() ? 1500 : 100);
        setTouchTimeout(timeout);
      }
    } catch (e) {
      console.log(e);
    }
  }

  return (
    <div className="relative flex flex-col w-full h-full" onMouseLeave={isMobile() ? () => { } : chartTouchEnd}>
      <svg ref={ref} className="absolute w-full h-full overflow-visible" onMouseMove={chartMouseMove} onTouchStart={chartTouchStart} onTouchMove={chartTouchMove} onTouchEnd={chartTouchEnd} onTouchCancel={chartTouchEnd}></svg>

      {/** Max value target icon */}
      <TfiTarget
        onMouseMove={chartMouseMove}
        size={isMobile() ? 15 : 18}
        style={{ top: `${maxValueDataPoint?.yPosition?.toFixed(0)}px`, left: `${maxValueDataPoint?.xPosition?.toFixed(0)}px` }}
        className={`absolute font-extrabold text-green-300 -translate-x-1/2 -translate-y-1/2 transition-all ease-in-out duration-1000 ${maxValueDataPoint === null ? "hidden" : ""}`}
      />

      {/** Min value target icon */}
      <TfiTarget
        onMouseMove={chartMouseMove}
        size={isMobile() ? 15 : 18}
        style={{ top: `${minValueDataPoint?.yPosition?.toFixed(0)}px`, left: `${minValueDataPoint?.xPosition?.toFixed(0)}px` }}
        className={`absolute font-extrabold text-red-300 -translate-x-1/2 -translate-y-1/2 transition-all ease-in-out duration-1000 ${minValueDataPoint === null ? "hidden" : ""}`}
      />

      {/** Tooltip target icon */}
      <TfiTarget
        onMouseMove={chartMouseMove}
        size={isMobile() ? 15 : 18}
        style={{ top: `${toolTip?.yPosition?.toFixed(0)}px`, left: `${toolTip?.xPosition?.toFixed(0)}px` }}
        className={`absolute font-extrabold text-orange-200 -translate-x-1/2 -translate-y-1/2 transition-opacity ease-in-out duration-200 ${showToolTipOpacity === false ? " opacity-0 " : " opacity-100 "} ${showToolTipVisible ? " " : " invisible "}`}
      />
      <div
        onMouseMove={chartMouseMove}
        style={{ left: `${toolTip?.xPosition?.toFixed(0)}px` }}
        className={`absolute top-1/2 -translate-y-1/2 ${toolTip?.side === "right" ? "translate-x-1/4" : "-translate-x-[125%]"} p-3 rounded-xl min-w-28 w-fit h-fit flex flex-col border border-x-neutral-200/50 border-y-2 border-y-neutral-200/70 backdrop-blur-sm transition-all ease-in-out duration-100 ${showToolTipOpacity === false ? " opacity-0 " : " opacity-100 "} ${showToolTipVisible ? " " : " invisible "}"`}>
        <div className="items-center text-center text-nowrap">
          <div className="flex text-center w-full">
            <TfiTarget className="w-full text-orange-200 text-xl lg:text-2xl" />
          </div>
          <div className="w-full text-sm lg:text-base underline underline-offset-4 pt-1.5">
            {props.xDescription}
          </div>
          <div className="w-full text-sm lg:text-base">
            {toolTip?.xValue?.toLocaleDateString()}
          </div>
          <div className="w-full text-sm lg:text-base underline underline-offset-4 pt-1.5">
            {props.zScoreEnabled ? "Desvios Padrões" : props.yDescription}
          </div>
          <div className="w-full text-sm lg:text-base">
            {props.zScoreEnabled ? (((toolTip?.yValue ?? 0) > 0 ? "+ " : "- ") + Math.abs(toolTip?.yValue ?? 0).toFixed(2) + " σ") : FormatValue(toolTip?.yValue, props.yFormat)}
          </div>
          <div className={`w-28 lg:w-44 border-t mt-1 pt-1 text-xs lg:text-sm text-start whitespace-pre-line ${toolTip?.label !== undefined && toolTip?.label !== "" && !props.zScoreEnabled ? "" : "hidden"}`}>
            {toolTip?.label !== undefined && toolTip?.label !== "" ? toolTip.label : ""}
          </div>
        </div>
      </div>

    </div>
  )
};

export default LineChart;
